import {
  exportToExcel,
  exportToPdf,
  ExcelExportOptions,
  PdfExportOptions,
  ExcelSummaryMetric,
} from './exportUtils';
import {
  CashbookRow,
  CashbookResponse,
  formatDisplayDate,
  formatINR,
  sourceLabel,
} from './cashbookCalc';

interface CashbookExportArgs {
  rows: CashbookRow[];
  totals?: CashbookResponse['totals'];
  opening_balance?: number;
  from?: string | null;
  to?: string | null;
  orgName?: string;
  accountName?: string;
}

export function buildCashbookExcelOptions({
  rows,
  totals,
  opening_balance = 0,
  from,
  to,
  orgName,
  accountName,
}: CashbookExportArgs): ExcelExportOptions {
  const dateRangeStr =
    from && to
      ? `${formatDisplayDate(from)} to ${formatDisplayDate(to)}`
      : 'All Time';
  const filterDesc = accountName ? `${accountName} · Period: ${dateRangeStr}` : `Period: ${dateRangeStr}`;

  const summaryMetrics: ExcelSummaryMetric[] = [];
  if (totals) {
    summaryMetrics.push({ label: 'Opening Balance', value: opening_balance, format: 'currency' });
    summaryMetrics.push({ label: 'Total Inflow (Income)', value: totals.income, format: 'currency' });
    summaryMetrics.push({ label: 'Total Outflow (Expense)', value: totals.expense, format: 'currency' });
    summaryMetrics.push({ label: 'Net Period Change', value: totals.income - totals.expense, format: 'currency' });
    summaryMetrics.push({ label: 'Closing Balance', value: totals.closing_balance, format: 'currency' });
  }

  const columns = [
    { header: 'Date', key: 'date', width: 14, format: 'text' as const },
    { header: 'Particular / Description', key: 'particular', width: 32, format: 'text' as const },
    { header: 'Type', key: 'type', width: 18, format: 'text' as const },
    { header: 'Party / Reference', key: 'party', width: 24, format: 'text' as const },
    { header: 'Account', key: 'account', width: 20, format: 'text' as const },
    { header: 'Income (₹)', key: 'income', width: 16, format: 'currency' as const },
    { header: 'Expense (₹)', key: 'expense', width: 16, format: 'currency' as const },
    { header: 'Balance (₹)', key: 'balance', width: 18, format: 'currency' as const },
  ];

  const data = rows.map((r) => [
    formatDisplayDate(r.transaction_date),
    r.particular,
    r.transaction_type.replace(/_/g, ' '),
    sourceLabel(r),
    r.account_name ?? '—',
    r.income_amount > 0 ? r.income_amount : 0,
    r.expense_amount > 0 ? r.expense_amount : 0,
    r.running_balance,
  ]);

  return {
    fileName: `Cashbook_${new Date().toISOString().split('T')[0]}`,
    sheetName: 'Cashbook',
    orgName,
    filtersSummary: filterDesc,
    columns,
    headers: columns.map((c) => c.header),
    data,
    includeTotalsRow: true,
    summaryMetrics: summaryMetrics.length > 0 ? summaryMetrics : undefined,
  };
}

export function buildCashbookPdfOptions({
  rows,
  totals,
  opening_balance = 0,
  from,
  to,
  orgName,
  accountName,
}: CashbookExportArgs): PdfExportOptions {
  const dateRangeStr =
    from && to
      ? `${formatDisplayDate(from)} to ${formatDisplayDate(to)}`
      : 'All Time';

  const summaryLines: { label: string; value: string }[] = [];
  if (totals) {
    summaryLines.push({ label: 'Opening Balance', value: formatINR(opening_balance) });
    summaryLines.push({ label: 'Total In', value: formatINR(totals.income) });
    summaryLines.push({ label: 'Total Out', value: formatINR(totals.expense) });
    summaryLines.push({ label: 'Closing Balance', value: formatINR(totals.closing_balance) });
  }

  const columns = [
    { header: 'Date', key: 'date' },
    { header: 'Particular', key: 'particular' },
    { header: 'Type', key: 'type' },
    { header: 'Party / Ref', key: 'party' },
    { header: 'Account', key: 'account' },
    { header: 'In (₹)', key: 'in' },
    { header: 'Out (₹)', key: 'out' },
    { header: 'Balance (₹)', key: 'bal' },
  ];

  const tableRows = rows.map((r) => [
    formatDisplayDate(r.transaction_date),
    r.particular,
    r.transaction_type.replace(/_/g, ' '),
    sourceLabel(r),
    r.account_name ?? '—',
    r.income_amount > 0 ? `₹${r.income_amount.toFixed(2)}` : '—',
    r.expense_amount > 0 ? `₹${r.expense_amount.toFixed(2)}` : '—',
    `₹${r.running_balance.toFixed(2)}`,
  ]);

  return {
    fileName: `Cashbook_${new Date().toISOString().split('T')[0]}`,
    title: 'Cashbook Statement',
    subtitle: `${orgName ? orgName + ' · ' : ''}${accountName ? accountName + ' · ' : ''}Period: ${dateRangeStr}`,
    columns,
    headers: columns.map((c) => c.header),
    rows: tableRows,
    summaryLines,
    orientation: 'landscape',
  };
}

export function exportCashbookExcel(args: CashbookExportArgs): void {
  exportToExcel(buildCashbookExcelOptions(args));
}

export function exportCashbookPdf(args: CashbookExportArgs): void {
  exportToPdf(buildCashbookPdfOptions(args));
}
